"use client";

import { useEffect, useRef } from "react";
import { Button } from "@farm-junction/design-library";
import type { GalleryCategory, GalleryCategoryId, GallerySubcategory } from "./types";

type GalleryFiltersProps = {
    variant: "page" | "popup";
    categories: GalleryCategory[];
    subcategories: GallerySubcategory[];
    counts: Record<GalleryCategoryId, number>;
    activeCategory: GalleryCategoryId;
    activeSubcategory: string;
    onCategoryChange: (id: GalleryCategoryId) => void;
    onSubcategoryChange: (id: string) => void;
    /** Page variant only: render just the category tabs or just the sub-category pills. */
    show?: "all" | "categories" | "subcategories";
};

const PAGE_SUBCATEGORY_CLASS =
    "flex max-w-none gap-2 overflow-x-auto bg-[#F9F9F9] p-2 rounded-full border border-gray-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
const PAGE_SUBCATEGORY_BUTTON =
    "shrink-0 text-sm font-medium !border !border-gray-200 !rounded-full !bg-white !text-[#373737] hover:!bg-[#008C8B] hover:!text-white hover:!border-[#008C8B] px-3.5 py-1.5";
const PAGE_SUBCATEGORY_BUTTON_ACTIVE = "!border-b-0 !text-white !bg-[#008C8B] !border-[#008C8B]";

const POPUP_SUBCATEGORY_CLASS =
    "flex w-full min-w-0 max-w-full gap-1 overflow-x-auto rounded-full bg-white p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
const POPUP_SUBCATEGORY_BUTTON =
    "shrink-0 text-sm font-medium !border-none !rounded-full !bg-transparent !text-[#373737] hover:!bg-[#F5F5F5] hover:!text-[#111111] px-3.5 py-1.5";
const POPUP_SUBCATEGORY_BUTTON_ACTIVE =
    "!border-b-0 !text-white !bg-[#008C8B] hover:!bg-[#008C8B] hover:!text-white md:!bg-[#111111] md:hover:!bg-[#111111]";

const ICON_CLASS = "h-4 w-4 shrink-0 stroke-current";

function AllIcon() {
    return (
        <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="3" width="8" height="8" rx="1.5" strokeWidth="1.8" />
            <rect x="13" y="3" width="8" height="8" rx="1.5" strokeWidth="1.8" />
            <rect x="3" y="13" width="8" height="8" rx="1.5" strokeWidth="1.8" />
            <rect x="13" y="13" width="8" height="8" rx="1.5" strokeWidth="1.8" />
        </svg>
    );
}

function ExteriorIcon() {
    return (
        <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 15h16l-1.4-4.2A3 3 0 0 0 15.7 9H8.3a3 3 0 0 0-2.9 1.8L4 15Z" strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M3 15h18v2.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5V15Z" strokeWidth="1.7" />
            <circle cx="7.5" cy="18.5" r="1.2" className="fill-current" />
            <circle cx="16.5" cy="18.5" r="1.2" className="fill-current" />
        </svg>
    );
}

function InteriorIcon() {
    return (
        <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="7.5" strokeWidth="1.7" />
            <circle cx="12" cy="12" r="2.2" strokeWidth="1.7" />
            <path d="M12 4.5V7M12 17v2.5M4.5 12H7M17 12h2.5" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
    );
}

function ColourIcon() {
    return (
        <svg className={ICON_CLASS} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="8" cy="10" r="3.2" className="fill-current" />
            <circle cx="16" cy="10" r="3.2" className="fill-current opacity-80" />
            <circle cx="12" cy="15.5" r="3.2" className="fill-current opacity-60" />
        </svg>
    );
}

function CategoryIcon({ icon }: { icon: GalleryCategory["icon"] }) {
    if (icon === "exterior") return <ExteriorIcon />;
    if (icon === "interior") return <InteriorIcon />;
    if (icon === "colour") return <ColourIcon />;
    return <AllIcon />;
}

export default function ImagePageFilters({
    variant,
    categories,
    subcategories,
    counts,
    activeCategory,
    activeSubcategory,
    onCategoryChange,
    onSubcategoryChange,
    show = "all",
}: GalleryFiltersProps) {
    const categoryListRef = useRef<HTMLDivElement>(null);
    const subcategoryListRef = useRef<HTMLDivElement>(null);
    const visibleSubcategories = activeCategory === "colour"
        ? []
        : subcategories.filter((item) => item.id === "all" || activeCategory === "all" || item.category === activeCategory);

    useEffect(() => {
        const scrollActiveTab = (root: HTMLDivElement | null) => {
            const active = root?.querySelector<HTMLElement>('[aria-selected="true"]');
            if (!root || !active) return;
            const rootRect = root.getBoundingClientRect();
            const activeRect = active.getBoundingClientRect();
            root.scrollBy({
                left: activeRect.left - rootRect.left - (rootRect.width - activeRect.width) / 2,
                behavior: "smooth",
            });
        };
        scrollActiveTab(categoryListRef.current);
        scrollActiveTab(subcategoryListRef.current);
    }, [activeCategory, activeSubcategory]);

    const subcategoryPills = visibleSubcategories.length > 1 ? (
        <div ref={subcategoryListRef} role="tablist" className={variant === "popup" ? POPUP_SUBCATEGORY_CLASS : PAGE_SUBCATEGORY_CLASS}>
            {visibleSubcategories.map((item) => {
                const isActive = item.id === activeSubcategory;
                return (
                    <Button
                        key={`${item.category}-${item.id}`}
                        variant="ghost"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => onSubcategoryChange(item.id)}
                        className={`${variant === "popup" ? POPUP_SUBCATEGORY_BUTTON : PAGE_SUBCATEGORY_BUTTON} ${
                            isActive
                                ? variant === "popup"
                                    ? POPUP_SUBCATEGORY_BUTTON_ACTIVE
                                    : PAGE_SUBCATEGORY_BUTTON_ACTIVE
                                : ""
                        }`}
                    >
                        {item.label}
                    </Button>
                );
            })}
        </div>
    ) : null;

    if (variant === "popup") {
        return (
            <div className="mt-1 flex w-full max-w-[682px] flex-col items-center gap-3 md:mt-6">
                <div ref={categoryListRef} className="flex w-full max-w-full gap-1 overflow-x-auto rounded-full bg-[#3A3A3A] p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:w-auto md:max-w-full">
                    {categories.map((category) => {
                        const isActive = category.id === activeCategory;
                        return (
                            <Button
                                key={category.id}
                                variant="ghost"
                                aria-selected={isActive}
                                role="tab"
                                onClick={() => onCategoryChange(category.id)}
                                className={`shrink-0 gap-2 rounded-full border-none px-3 py-2 text-sm font-medium md:px-4 ${
                                    isActive
                                        ? "!bg-white !text-[#111111] hover:!bg-white hover:!text-[#111111]"
                                        : "!bg-transparent !text-[#C8C8C8] hover:!bg-white/10 hover:!text-white"
                                }`}
                            >
                                <CategoryIcon icon={category.icon} />
                                {category.label} ({counts[category.id]})
                            </Button>
                        );
                    })}
                </div>

                {subcategoryPills ? <div className="w-full min-w-0">{subcategoryPills}</div> : null}
            </div>
        );
    }

    if (show === "subcategories") {
        return subcategoryPills ? <div className="w-full">{subcategoryPills}</div> : null;
    }

    return (
        <div className="w-full">
            <div ref={categoryListRef} className="flex max-w-full gap-0 overflow-x-auto border-b border-b-[#008C8B26] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {categories.map((category) => {
                    const isActive = category.id === activeCategory;
                    return (
                        <Button
                            key={category.id}
                            variant="ghost"
                            aria-selected={isActive}
                            role="tab"
                            onClick={() => onCategoryChange(category.id)}
                            className={`shrink-0 gap-2 rounded-none border-x-0 border-t-0 border-b-2 px-3 py-3 text-sm font-medium md:px-4 ${
                                isActive
                                    ? "!border-[#008C8B] !bg-[#008C8B0D] !text-[#008C8B] hover:!bg-[#008C8B0D] hover:!text-[#008C8B]"
                                    : "!border-transparent !bg-transparent !text-[#373737] hover:!bg-[#008C8B0D] hover:!text-[#008C8B] hover:!border-[#008C8B]"
                            }`}
                        >
                            <CategoryIcon icon={category.icon} />
                            {category.label} ({counts[category.id]})
                        </Button>
                    );
                })}
            </div>

            {show === "all" && subcategoryPills ? <div className="mt-3">{subcategoryPills}</div> : null}
        </div>
    );
}
